import { useCallback, useEffect, useMemo } from "react";
import { Box, Stack, useTheme, Typography } from "@mui/material";
import { AddCircle } from "@mui/icons-material";
import { useHistory } from "react-router-dom";

import AtomAssistLogo from "src/views/assistant/components/AssistantLogo";
import AAAssistantText from "src/views/assistant/components/AAAssistantText";
import AAHeaderButton, {
  useIsHeaderCompact,
} from "src/views/assistant/components/header/AAHeaderButton";
import ChatContainer from "src/reusable/Chat/ChatContainer";
import { ChatWrapperContainer } from "src/reusable/Chat/StyledContainers/StyledContainers";
import { buildMessageTree } from "src/reusable/Chat/ChatStreaming/services/chatHelpers";
import useLoadDialogue from "src/views/assistant/hooks/useLoadDialogue";
import useStartChatOnMount from "src/reusable/Chat/hooks/useStartChatOnMount";
import {
  useActiveDatasetIds,
  useChatStore,
  useSourceDataRecordIds,
} from "src/views/assistant/stores/ChatStore";
import { useFfnRecordStore } from "src/views/assistant/stores/FfnRecordStore";
import WorkflowChatInput from "src/views/products/workflow/components/WorkflowChatInput";
import { FIFTY_FIFTY_NINE_PRODUCT } from "./fiftyFiftyNineProduct";
import { useFfnChat } from "./hooks/useFfnChat";
import { ChatStreamProvider } from "src/reusable/Chat/ChatStreaming/context/ChatStreamContext";
import FfnSidebar from "./components/FfnSidebar";

const FfnChat = ({
  dialogueId,
  initialUserMessage,
}: {
  dialogueId: number | undefined;
  initialUserMessage?: string;
}) => {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useIsHeaderCompact();

  const {
    config: chatConfig,
    setActiveDialogueId,
    setDialogue: setStoreDialogue,
    setInitialUserMessage,
  } = useChatStore();

  const activeDatasetIds = useActiveDatasetIds();
  const sourceDataRecordIds = useSourceDataRecordIds();

  useEffect(() => {
    if (dialogueId) {
      setActiveDialogueId(dialogueId);
      // A different dialogue's record must not bleed into this chat's panel
      // (same contract as AtomAssistPage's clearForDialogue on switch).
      useFfnRecordStore.getState().clearForDialogue(dialogueId);
      // 50.59 has no document-selection surface, so any scope left in the
      // shared store came from another product and would narrow this review.
      useChatStore.getState().setSourceDataRecordIds([]);
    }
  }, [dialogueId, setActiveDialogueId]);

  const { dialogue: loadedDialogue } = useLoadDialogue({ initialUserMessage });

  const dialogueTree = useMemo(() => {
    if (loadedDialogue?.messages && loadedDialogue.messages.length > 0) {
      return buildMessageTree(loadedDialogue.messages);
    }
    return [];
  }, [loadedDialogue]);

  const datasetsToUse = useMemo(
    () => activeDatasetIds.map((id) => ({ id })),
    [activeDatasetIds],
  );

  const chatConfigMemo = useMemo(() => {
    return {
      dialogueId,
      modelId: chatConfig?.modelId,
      modelVersionNum: chatConfig?.modelVersionNum,
      datasetsForSearchContext: datasetsToUse,
      sourceDataRecordIds:
        sourceDataRecordIds.length > 0 ? sourceDataRecordIds : undefined,
      numSearchResults: chatConfig?.topSearchResults || 5,
      textGenModelParams: chatConfig?.chatControlMetadata,
      systemPrompt: chatConfig?.systemPrompt,
      toolCalling: true,
      chatConfigId: chatConfig?.chatConfigId,
      // The per-turn product value is what activates the workflow tools,
      // the record context injector, and the lead resolver server-side.
      product: FIFTY_FIFTY_NINE_PRODUCT.productKey,
    };
  }, [
    dialogueId,
    chatConfig?.modelId,
    chatConfig?.modelVersionNum,
    chatConfig?.chatControlMetadata,
    chatConfig?.systemPrompt,
    chatConfig?.chatConfigId,
    chatConfig?.topSearchResults,
    datasetsToUse,
    sourceDataRecordIds,
  ]);

  const {
    dialogue,
    responseSources,
    setResponseSources,
    terminateConnection,
    isConnected: isChatConnected,
    isConnectionError: isChatConnectionError,
    streamError: chatStreamError,
    isEnded: isChatEnded,
    isLoading: isChatLoading,
    isMessageStreamStopped: isChatMessageStreamStopped,
    isTurnActive,
    emptySubmission,
    clearChatErrors,
    sendUserMessage,
    resendUserMessage,
    regenerateResponse,
    setAsPreferredMessage,
    setDialogue,
    reconstructArtifactsFromMessages,
    reset: resetChatStream,
    tokenUsage,
    setTokenUsage,
    retryConnection,
    lastDialogueId,
    activeSubAgents,
    store: chatStore,
  } = useFfnChat(chatConfigMemo, dialogueTree);

  useEffect(() => {
    // Live session state beats the REST snapshot: re-hydrating mid-run would
    // clobber streamed parts (same guard AtomAssistPage carries).
    if (
      lastDialogueId === dialogueId &&
      (dialogue.length > 0 || isChatConnected)
    ) {
      return;
    }
    // Keyed on the tree itself, not its length, so an edit or regeneration that
    // leaves the count unchanged still syncs — and an empty tree is pushed, so
    // the previous dialogue's messages do not survive a hop to a fresh one.
    setDialogue(dialogueTree);
    setStoreDialogue(dialogueTree);
    if (dialogueTree.length > 0) {
      // Replays stored tool responses, which is what repopulates the 50.59
      // record panel on reload.
      reconstructArtifactsFromMessages(dialogueTree);
    }
  }, [
    dialogueTree,
    dialogueId,
    lastDialogueId,
    dialogue.length,
    isChatConnected,
    setDialogue,
    setStoreDialogue,
    reconstructArtifactsFromMessages,
  ]);

  useEffect(() => {
    if (loadedDialogue?.config?.token_usage) {
      setTokenUsage(loadedDialogue.config.token_usage);
    }
  }, [loadedDialogue?.config?.token_usage, dialogueId, setTokenUsage]);

  useStartChatOnMount({
    initialUserMessage,
    clearInitialMessage: () => setInitialUserMessage(undefined),
    emptySubmission,
    sendUserMessage,
  });

  const onNewChat = useCallback(() => {
    useChatStore.getState().clearUploadedFiles();
    history.push(FIFTY_FIFTY_NINE_PRODUCT.basePath);
  }, [history]);

  useEffect(() => {
    return () => {
      resetChatStream();
      terminateConnection();
      clearChatErrors();
    };
  }, [resetChatStream, terminateConnection, clearChatErrors]);

  return (
    <>
      <Box
        id="ffn-chat-header"
        sx={{
          height: "55px",
          bgcolor: "background.paper",
          width: "100%",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.palette.divider}`,
          px: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {FIFTY_FIFTY_NINE_PRODUCT.displayName}
        </Typography>
        <AAHeaderButton
          icon={<AddCircle />}
          label="New review"
          onClick={onNewChat}
          tooltip="New review"
          variant="contained"
          isMobile={isMobile}
          data-testid="ffn-btn-new-chat"
          data-attr="assistant:btn:fifty-fifty-nine-new-chat"
        />
      </Box>
      <ChatStreamProvider store={chatStore}>
        <Stack
          direction="row"
          sx={{ height: "calc(100vh - 97px)", overflow: "hidden" }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <ChatWrapperContainer sx={{ flex: 1 }}>
              <ChatContainer
                activeSubAgents={activeSubAgents}
                dialogueId={dialogueId || undefined}
                dialogue={dialogue}
                isChatConnectionError={isChatConnectionError}
                chatStreamError={chatStreamError}
                isChatConnected={isChatConnected}
                isChatEnded={isChatEnded}
                isChatLoading={isChatLoading}
                isChatMessageStreamStopped={isChatMessageStreamStopped}
                startNewChat={onNewChat}
                resendUserMessage={resendUserMessage}
                regenerateResponse={regenerateResponse}
                setAsPreferredMessage={setAsPreferredMessage}
                onRetryConnection={lastDialogueId ? retryConnection : undefined}
                ChatPromptInput={
                  <WorkflowChatInput
                    chatStreamError={chatStreamError}
                    terminateConnection={terminateConnection}
                    sendUserMessage={sendUserMessage}
                    isChatConnectionError={isChatConnectionError}
                    isChatLoading={isChatLoading}
                    isTurnActive={isTurnActive}
                    tokenUsage={tokenUsage}
                    product={FIFTY_FIFTY_NINE_PRODUCT.productKey}
                    placeholder="Ask the 50.59 Evaluation Lead…"
                  />
                }
                BackgroundComponent={
                  <AtomAssistLogo
                    lowerMessage={"How can I help with your 50.59 review?"}
                  />
                }
                CustomAssistantText={AAAssistantText}
                CustomAssistantTextProps={{
                  isChatConnected,
                  responseSources,
                  onSourcesUpdate: setResponseSources,
                }}
              />
            </ChatWrapperContainer>
          </Box>

          <FfnSidebar
            responseSources={responseSources}
            dialogueId={dialogueId}
          />
        </Stack>
      </ChatStreamProvider>
    </>
  );
};

export default FfnChat;
